import { type SectionType, SectionTypes } from "../types/templateTypes";

export enum EzyVetCategories {
    NO_MAPPING = "No Mapping",
    HISTORY = "history",
    VITALS = "vitals",
    EXAM = "exam",
    ASSESSMENT = "assessment",
    PLAN = "plan",
    REVISIT = "revisit",
    THERAPEUTICS = "therapeutics",
    PERTINENT_HISTORY = "pertinent history",
    IN_CLINIC_NOTES = "in clinic notes",
}

export const HumanReadableEzyVetCategories: Record<string,string>  = {
    [EzyVetCategories.HISTORY]: "Subjective / History",
    [EzyVetCategories.EXAM]: "Objective / Physical Exam",
    [EzyVetCategories.VITALS]: "Vitals / T.P.R / Health Status",
}  

export function EzyVetCategoryToDisplayName(category: string): string {
    return HumanReadableEzyVetCategories[category] || category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, " ");
}

export enum EzyVetVitalsCategories {
    NO_MAPPING = "No Mapping",
    WEIGHT = "weight",
    HEARTRATE = "heart rate",
    TEMPERATURE = "temperature",
    RESPIRATORYRATE = "respiratory rate",
    BLOODPRESSURE = "blood pressure",
    BCS = "BCS",
    PAINSCORE = "pain score",
    DENTALSCORE = "dental score",
    ATTITUDE = "attitude",
    CRT = "CRT",
    MM = "MM",
    PULSEQUALITY = "pulse quality",
    RESPIRATORYEFFORT = "respiratory effort",
    NOTES = "notes",
}

export const HumanReadableEzyVetVitalsCategories: Record<string, string> = {
    [EzyVetVitalsCategories.NOTES]: "Notes / Demeanor",
}  

export function EzyVetVitalsCategoryToDisplayName(category: string): string {
    return HumanReadableEzyVetVitalsCategories[category] || category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, " ");
}

export function categoryToInputName(category: EzyVetCategories) {
    switch (category) {
        case EzyVetCategories.HISTORY:
            return "visithistorydata_comments"
        case EzyVetCategories.ASSESSMENT:
            return "consultassessmentdata_notes"
        case EzyVetCategories.EXAM:
            return "visitexamdata_comments"
        case EzyVetCategories.PLAN:
            return "consultplandata_notes"
        case EzyVetCategories.THERAPEUTICS:
            return "plantherapeuticdata_specifics"
        case EzyVetCategories.PERTINENT_HISTORY:
            return "consultpertinenthistorydata_notes"
        case EzyVetCategories.IN_CLINIC_NOTES:
            return "inclinicdata_notes"
        //case EzyVetCategories.OWNER_COMMUNICATIONS:
        // return ""
        case EzyVetCategories.REVISIT:
            return "revisitdata_notes"
    }
}

export interface EzyVetSection {
    name: string
    ezyvet_section_name: string
    category: EzyVetCategories | undefined
    inputs: { [key: string]: EzyVetSubInput }
}

export interface EzyVetSubInput {
    name: string
    inputName: string
}

export function inputNameToCategory(inputName: string): EzyVetCategories {
    const mapping: Record<string, EzyVetCategories> = {
        "visithistorydata_comments": EzyVetCategories.HISTORY,
        "consultassessmentdata_notes": EzyVetCategories.ASSESSMENT,
        "visitexamdata_comments": EzyVetCategories.EXAM,
        "consultplandata_notes": EzyVetCategories.PLAN,
        "plantherapeuticdata_specifics": EzyVetCategories.THERAPEUTICS,
        "consultpertinenthistorydata_notes": EzyVetCategories.PERTINENT_HISTORY,
        "inclinicdata_notes": EzyVetCategories.IN_CLINIC_NOTES,
        "revisitdata_notes": EzyVetCategories.REVISIT
    };

    return mapping[inputName] || EzyVetCategories.NO_MAPPING;
}

export const EzyVetSectionMappings = {
    "visithistorydata_comments": "VisitHistory",
    "consultassessmentdata_notes": "ConsultAssessment",
    "visitexamdata_comments": "VisitExam",
    "consultplandata_notes": "ConsultPlan",
    "plantherapeuticdata_specifics": "Therapeutics",
    "consultpertinenthistorydata_notes": "ConsultPertinentHistory",
    "inclinicdata_notes": "InClinicNotes",
    "revisitdata_notes": "Revisit",
    "vitals": "HealthStatus"
}


export function saveEzyVetMapping(mapping:{[key:string]:EzyVetSection}, items:SectionType[]) {
    const processed_mapping: {[key:string] : {[key:string] : string | {[key:string] : {[key:string] : string }}}} = {}
    Object.keys(mapping).forEach((key) => {
      // check if key is of SectionType type General Instructions. If so, ignore it and continue to other sections
      const section = items.find((section) => section.name?.toLowerCase() === key.toLowerCase())
      if(section && [SectionTypes.GENERAL_INSTRUCTIONS, SectionTypes.DIAGRAM].includes(section.type)) return
      if (!mapping[key]) {
        console.warn(`Mapping for ${key} not found`);
        return
      }
      if(mapping[key].category !== EzyVetCategories.VITALS){
        processed_mapping[key.toLowerCase()] = {
          type:"one-to-one",
          displayName: mapping[key].ezyvet_section_name,
          button: `Add${mapping[key].ezyvet_section_name.replace(/[^a-zA-Z0-9]/g, '')}`,
          addButton: "Add",
          inputType: "textarea",
          inputName: categoryToInputName(mapping[key].category ?? EzyVetCategories.HISTORY) ?? "",
          inputFormat: "string"
        }
      }
      else{
        processed_mapping[key.toLowerCase()] = {
          type:"one-to-many",
          displayName: mapping[key].ezyvet_section_name,
          button: `Add${mapping[key].ezyvet_section_name.replace(/[^a-zA-Z0-9]/g, '')}`,
          addButton: "Add",
          inputName: "vitals",
          inputs: saveEzyVetInputs(mapping[key].inputs)
        }
      }
    })
    return processed_mapping
  }

export function saveEzyVetInputs(inputs: { [key: string]: EzyVetSubInput }) {
    const processed_inputs: { [key: string]: { [key: string]: string } } = {}
    Object.keys(inputs).forEach((key) => {
        let inputFormat = "string"
        let inputType = "input"
        const inputName = inputs[key]?.inputName;
        if (
            ([
                EzyVetVitalsCategories.WEIGHT,
                EzyVetVitalsCategories.TEMPERATURE,
                EzyVetVitalsCategories.HEARTRATE,
                EzyVetVitalsCategories.RESPIRATORYRATE,
                EzyVetVitalsCategories.BCS,
                EzyVetVitalsCategories.PAINSCORE,
                EzyVetVitalsCategories.DENTALSCORE,
                EzyVetVitalsCategories.BLOODPRESSURE,
            ] as (string | undefined)[]).includes(inputName)
        ) {
          inputFormat = "number";
        }

        if (
            ([
                EzyVetVitalsCategories.ATTITUDE,
                EzyVetVitalsCategories.CRT,
                EzyVetVitalsCategories.MM,
                EzyVetVitalsCategories.RESPIRATORYEFFORT,
                EzyVetVitalsCategories.PULSEQUALITY,
            ] as (string | undefined)[]).includes(inputName)
        ) {
          inputType = "select";
        }

        if (([EzyVetVitalsCategories.NOTES] as (string | undefined)[]).includes(inputName)) {
            inputType = "textarea";
        }

        processed_inputs[key.toLowerCase()] = {
            inputType: inputType,
            inputFormat: inputFormat,
            inputName: `healthstatusdata_${inputName
                    ?.replace(/\s/g, "")
                    .replace("notes", "demeanor")
                    .toLowerCase() ?? ""
                }`,
        };
    })
    return processed_inputs
}

export const naiveMappingAttempt = (name: string): EzyVetCategories => {
    if(["exam", "physical", "objective"].some((item) => name.toLowerCase().includes(item.toLowerCase()))){
        return EzyVetCategories.EXAM
    }
    if(["assessment", "assessment plan", "assess"].some((item) => name.toLowerCase().includes(item.toLowerCase()))){
        return EzyVetCategories.ASSESSMENT
    }
    if(["plan", "treatment plan", "treatment"].some((item) => name.toLowerCase().includes(item.toLowerCase()))){
        return EzyVetCategories.PLAN
    }
    if(["history", "subjective", "subjective history"].some((item) => name.toLowerCase().includes(item.toLowerCase()))){
        return EzyVetCategories.HISTORY
    }
    if(["revisit", "recheck", "recheck plan"].some((item) => name.toLowerCase().includes(item.toLowerCase()))){
        return EzyVetCategories.REVISIT
    }
    if(["owner communications", "owner communication", "owner comms"].some((item) => name.toLowerCase().includes(item.toLowerCase()))){
        return EzyVetCategories.THERAPEUTICS
    }
    if(["pertinent history", "pertinent history notes"].some((item) => name.toLowerCase().includes(item.toLowerCase()))){
        return EzyVetCategories.PERTINENT_HISTORY
    }
    if(["in clinic notes", "in clinic note"].some((item) => name.toLowerCase().includes(item.toLowerCase()))){
        return EzyVetCategories.IN_CLINIC_NOTES
    }
    if(["vitals", "health status", "health status notes"].some((item) => name.toLowerCase().includes(item.toLowerCase()))){
        return EzyVetCategories.VITALS
    }
    return EzyVetCategories.NO_MAPPING;
}

const naiveVitalMappingAttempt = (entryName: string): EzyVetVitalsCategories => {
    if (["weight", "wt", "bw", "body weight"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.WEIGHT;
    }
    if (["heart rate", "hr", "pulse", "pulse rate"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.HEARTRATE;
    }
    if (["temp", "temperature"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.TEMPERATURE;
    }
    if (["resp", "respiratory rate", "breathing rate", "rr"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.RESPIRATORYRATE;
    }
    if (["bp", "blood pressure", "systolic", "diastolic"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.BLOODPRESSURE;
    }
    if (["bcs", "body condition", "body condition score"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.BCS;
    }
    if (["pain", "pain score"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.PAINSCORE;
    }
    if (["dental", "dental score", "teeth"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.DENTALSCORE;
    }
    if (["attitude", "demeanor", "mood"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.ATTITUDE;
    }
    if (["crt", "capillary refill", "capillary refill time"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.CRT;
    }
    if (["mm", "mucous membrane", "mucous membranes"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.MM;
    }
    if (["pulse quality", "pulse qual"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.PULSEQUALITY;
    }
    if (["respiratory effort", "resp effort", "breathing effort"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.RESPIRATORYEFFORT;
    }
    if (["notes", "comment", "comments", "additional", "additional vitals"].some((item) => entryName.toLowerCase().includes(item.toLowerCase()))) {
        return EzyVetVitalsCategories.NOTES;
    }
    return EzyVetVitalsCategories.NO_MAPPING;
}

export const naiveVitalsMappingAttempt = (entryNames: string[]): {[key: string]: EzyVetSubInput} => {
    if (!entryNames) return {}

    const entryDict: {[key: string]: EzyVetSubInput} = {};
    for (const entryName of entryNames) {
        entryDict[entryName] = {
            name: entryName,
            inputName: naiveVitalMappingAttempt(entryName),
        };
    }

    return entryDict;
}